import React, { useEffect, useState } from 'react';
import {
  useFormContext,
  Controller,
  FieldErrorsImpl,
  DeepRequired,
  FieldValues
} from 'react-hook-form';
import { createStyles, TextField, makeStyles, Theme } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TAttachableObject, TSelectItem } from '../../../types/common';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';

type TProps = {
  name: any;
  listDatas: TSelectItem[];
  style?: React.CSSProperties;
  onChange?: any;
  defaultValue?: TSelectItem;
  required?: boolean;
  error?: boolean;
  errorMessage?: React.ReactNode;
  fullWidth?: boolean;
  label?: string;
  value?: TSelectItem;
  disabled?: boolean;
  className?: string;
  staffData?: TSelectItem | null;
  inactiveListDatas?: TSelectItem[] | null;
  sortHasProcess?: boolean;
};

export const SiteProcessSelectfield: React.VFC<TProps> = React.memo(
  (props: TProps) => {
    // react-hook-fomrを使用する前提のcomponent
    const { register, control } = useFormContext();
    const height = 34;

    const classes = useStyles();

    // https://mui.com/components/text-fields/#main-content の必要なpropsを追加する
    const {
      name,
      listDatas,
      style,
      onChange,
      defaultValue,
      required = false,
      error,
      errorMessage,
      fullWidth = false,
      label,
      value,
      disabled,
      className,
      staffData,
      inactiveListDatas,
      sortHasProcess = false
    } = props;

    const updatedItems = listDatas.map(item => ({
      ...item,
      ...(item.hasProcess !== undefined && {
        hasProcessTag: item.hasProcess ? '作成済一覧' : '未作成一覧'
      })
    }));

    const [options, setOptions] = useState(updatedItems);
    // strictmodeが有効の状態でもlistDatasに値が入るように
    useEffect(() => {
      setOptions(updatedItems);
    }, [listDatas]);

    // valueはundefindでない場合のみ使う
    const extraProps: TAttachableObject = {};
    if (value) extraProps.value = value;

    const handleInputChange = (event: any, inputValue: any) => {
      // タスク編集・工程編集の担当者プルダウン用
      if (staffData) {
        if (inputValue && inputValue !== '' && inputValue !== '--') {
          const isExistingLabel = listDatas.some(
            item =>
              item.label === inputValue ||
              item.label === staffData.label ||
              item.label === `${staffData.label}(無効なユーザー)`
          );

          if (!isExistingLabel) {
            setOptions(updatedItems);
            return;
          }
        }

        // カレンダーの担当者プルダウン用
      } else if (inactiveListDatas) {
        if (inputValue && inputValue !== '' && inputValue !== '--') {
          const isExistingLabel = listDatas.some(
            item => item.label === inputValue
          );

          if (!isExistingLabel) {
            setOptions(updatedItems);
            return;
          }
        }
      }
      setOptions(updatedItems);
    };

    // 工程を作成済か否かでソートする
    const sortedOptions = React.useMemo(() => {
      if (sortHasProcess) {
        return options.sort((a, b) =>
          a.hasProcessTag!.localeCompare(b.hasProcessTag!, 'ja')
        );
      }
      return options;
    }, [options, sortHasProcess]);

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Autocomplete
            {...register(name, {
              required: required,
              validate: value => {
                if (required) {
                  return value.value != 0;
                }
                return true;
              }
            })}
            {...field}
            options={sortedOptions}
            autoHighlight
            classes={{
              inputRoot: classes.inputRoot // class name, e.g. `classes-nesting-root-x`
            }}
            className={`${classes.textField} ${className}`}
            onInputChange={handleInputChange}
            fullWidth={fullWidth}
            groupBy={
              sortHasProcess ? options => options.hasProcessTag : undefined
            }
            getOptionLabel={option => option?.label || ''}
            defaultValue={defaultValue}
            style={style}
            {...extraProps}
            onChange={(_, value) => {
              field.onChange(value);

              if (onChange) {
                onChange(value);
              }
            }}
            disabled={disabled}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={label}
                autoComplete='off'
                helperText={errorMessage}
                error={error}
                variant='outlined'
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                  style: {
                    height,
                    padding: '0 14px'
                  }
                }}
              />
            )}
            renderGroup={params => (
              <div key={params.key}>
                <ListSubheader
                  sx={{
                    color: 'black',
                    border: '2px solid black',
                    backgroundColor: '#f0f0f0',
                    px: 2,
                    py: 1
                  }}
                >
                  <Typography variant='subtitle1'>{params.group}</Typography>
                </ListSubheader>
                {params.children}
              </div>
            )}
          />
        )}
      />
    );
  }
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      backgroundColor: 'white',
      zIndex: 11
    },
    inputRoot: {
      padding: '0px !important'
    }
  })
);
